import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import cptExamService, {
  CPTExam,
  CPTDetectionResult,
  CPTSearchParams,
} from '../services/cptExamService';

// ── State ────────────────────────────────────────────────────────────────

export interface CPTExamState {
  // Search results
  searchResults: CPTExam[];
  searchTotal: number;
  searchLoading: boolean;
  searchError: string | null;

  // Selected CPT exam (manual or auto-detected)
  selectedExam: CPTExam | null;

  // Whether the user explicitly set or cleared the CPT (suppresses auto-detect)
  manuallySet: boolean;

  // Auto-detection result
  detection: CPTDetectionResult | null;
  detecting: boolean;
  detectionError: string | null;

  // Filter options
  modalities: string[];
  bodyRegions: string[];
  subspecialties: string[];
}

const initialState: CPTExamState = {
  searchResults: [],
  searchTotal: 0,
  searchLoading: false,
  searchError: null,
  selectedExam: null,
  manuallySet: false,
  detection: null,
  detecting: false,
  detectionError: null,
  modalities: [],
  bodyRegions: [],
  subspecialties: [],
};

// ── Async Thunks ─────────────────────────────────────────────────────────

export const searchCPTExams = createAsyncThunk(
  'cptExams/search',
  async (params: CPTSearchParams) => {
    return await cptExamService.searchExams(params);
  }
);

export const fetchModalities = createAsyncThunk(
  'cptExams/fetchModalities',
  async () => {
    return await cptExamService.getModalities();
  }
);

export const fetchBodyRegions = createAsyncThunk(
  'cptExams/fetchBodyRegions',
  async () => {
    return await cptExamService.getBodyRegions();
  }
);

export const fetchSubspecialties = createAsyncThunk(
  'cptExams/fetchSubspecialties',
  async () => {
    return await cptExamService.getSubspecialties();
  }
);

export const detectCPTFromTranscript = createAsyncThunk(
  'cptExams/detect',
  async ({ transcript, useLlm, sessionId }: { transcript: string; useLlm?: boolean; sessionId?: string }) => {
    return await cptExamService.detectFromTranscript(transcript, useLlm, sessionId);
  }
);

export const fetchCPTExam = createAsyncThunk(
  'cptExams/fetchExam',
  async ({ cptCode, bodyRegion, examName }: { cptCode: string; bodyRegion?: string; examName?: string }) => {
    return await cptExamService.getExam(cptCode, bodyRegion, examName);
  }
);

// ── Slice ────────────────────────────────────────────────────────────────

const cptExamSlice = createSlice({
  name: 'cptExams',
  initialState,
  reducers: {
    selectExam(state, action: PayloadAction<CPTExam | null>) {
      state.selectedExam = action.payload;
    },
    setManuallySet(state, action: PayloadAction<boolean>) {
      state.manuallySet = action.payload;
    },
    clearDetection(state) {
      state.detection = null;
      state.detectionError = null;
    },
    clearSearch(state) {
      state.searchResults = [];
      state.searchTotal = 0;
      state.searchError = null;
    },
    resetCPTState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // Search
    builder
      .addCase(searchCPTExams.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchCPTExams.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload.items;
        state.searchTotal = action.payload.total;
      })
      .addCase(searchCPTExams.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.error.message || 'Search failed';
      });

    // Detection
    builder
      .addCase(detectCPTFromTranscript.pending, (state) => {
        state.detecting = true;
        state.detectionError = null;
      })
      .addCase(detectCPTFromTranscript.fulfilled, (state, action) => {
        state.detecting = false;
        state.detection = action.payload;
      })
      .addCase(detectCPTFromTranscript.rejected, (state, action) => {
        state.detecting = false;
        state.detectionError = action.error.message || 'Detection failed';
      });

    // Note: fetchCPTExam does NOT auto-set selectedExam.
    // Callers must dispatch selectExam() explicitly after merging any laterality
    // from the detection result to avoid two sources of truth.

    // Filter options
    builder
      .addCase(fetchModalities.fulfilled, (state, action) => {
        state.modalities = action.payload;
      })
      .addCase(fetchBodyRegions.fulfilled, (state, action) => {
        state.bodyRegions = action.payload;
      })
      .addCase(fetchSubspecialties.fulfilled, (state, action) => {
        state.subspecialties = action.payload;
      });
  },
});

export const { selectExam, setManuallySet, clearDetection, clearSearch, resetCPTState } =
  cptExamSlice.actions;
export default cptExamSlice.reducer;
